import { useState, useEffect, useMemo, useRef } from "react";
import { Icon } from "@/lib/icons";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import InstallAppButton from "@/components/InstallAppButton";
import { ReportIssue } from "@/components/ReportIssue";
import { LogOut, X, Search } from "lucide-react";
import { buildRail, groupOfTarget, CODES_PINS } from "@/lib/navLayout";

export const Badge = ({ n, className = "" }) => (n ? <span className={`min-w-[18px] h-[18px] px-1 rounded-full bg-gold text-ink text-[10px] font-bold flex items-center justify-center ${className}`}>{n}</span> : null);

export function useRail(me) {
  const { persona } = useAuth();
  const [badges, setBadges] = useState({});
  useEffect(() => { api.get("/nav/badges").then((r) => setBadges(r.data.badges || {})).catch(() => {}); }, [me.nav]);
  return useMemo(() => buildRail(me.nav || [], { isSuper: !!me.user?.is_super_admin && !persona, badges }), [me, badges, persona]);
}

export const isActiveItem = (item, active) => item.target === active || (item.id === "codes_pins" && CODES_PINS.includes(active));

function PageList({ rail, group, query, active, onPick }) {
  const q = query.trim().toLowerCase();
  const lists = q ? rail.map((g) => ({ g, items: g.items.filter((i) => i.label.toLowerCase().includes(q)) })).filter((x) => x.items.length)
    : rail.filter((g) => !group || g.id === group.id).map((g) => ({ g, items: g.items }));
  if (!lists.length) return <div className="px-3 py-4 text-[14px] text-ink/50" data-testid="nav-search-empty">No screens match "{query}".</div>;
  return lists.map(({ g, items }) => (
    <div key={g.id} className="mb-2">
      {(q || !group) && <div className="px-3 pt-2 pb-1 text-[11px] uppercase tracking-widest font-bold text-ink/45">{g.label}</div>}
      {items.map((i) => (
        <button key={i.id} data-testid={`nav-${i.id}`} onClick={() => onPick(i)}
          className={`w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-left text-[15px] min-h-[42px] transition-colors ${isActiveItem(i, active) ? "bg-maroon/10 text-maroon font-semibold" : "text-ink hover:bg-ink/5"}`}>
          <span className="flex-1 truncate">{i.label}</span>
          <Badge n={i.badge} />
        </button>
      ))}
    </div>
  ));
}

function Flyout({ rail, group, active, onPick, onClose, full }) {
  const [query, setQuery] = useState("");
  return (
    <div data-testid={full ? "nav-mobile-menu" : "nav-flyout"}
      className={full ? "fixed inset-0 z-50 bg-cream flex flex-col animate-rise" : "absolute left-full top-0 h-full w-[264px] bg-cream border-r border-ink/15 shadow-2xl flex flex-col z-40 animate-rise"}>
      <div className="flex items-center justify-between px-4 pt-4 pb-3">
        <h2 className="font-zilla text-xl font-semibold text-ink">{full ? "Menu" : group.label}</h2>
        <button onClick={onClose} aria-label="Close menu" data-testid="nav-flyout-close" className="w-9 h-9 rounded-lg border border-ink/15 flex items-center justify-center text-ink/70 hover:bg-ink/5"><X className="w-4 h-4" /></button>
      </div>
      <div className="px-4 pb-3">
        <div className="flex items-center gap-2 bg-white border border-ink/15 rounded-lg px-3 min-h-[40px] focus-within:border-maroon/50">
          <Search className="w-4 h-4 text-ink/40" />
          <input autoFocus={!full} value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Find any screen" data-testid="nav-find-screen" className="bg-transparent flex-1 text-[14px] py-2 outline-none" />
        </div>
      </div>
      <nav className={`flex-1 overflow-y-auto px-2 ${full ? "pb-24" : "pb-4"}`}>
        <PageList rail={rail} group={full ? null : group} query={query} active={active} onPick={onPick} />
      </nav>
    </div>
  );
}

function SignedInBox({ me, active }) {
  const { logout } = useAuth();
  const [open, setOpen] = useState(false);
  const initials = me.user.name?.split(" ").map((s) => s[0]).slice(0, 2).join("");
  return (
    <div className="relative">
      <button onClick={() => setOpen((o) => !o)} data-testid="signed-in-box" className="w-full flex flex-col items-center gap-1 py-2 rounded-lg hover:bg-cream/10">
        {me.user.picture ? <img src={me.user.picture} alt="" className="w-8 h-8 rounded-full object-cover" />
          : <span className="w-8 h-8 rounded-full bg-olive text-ink font-bold flex items-center justify-center text-[12px]">{initials}</span>}
        <span className="text-[11px] text-cream/80 truncate max-w-[68px]" data-testid="sidebar-user-name">{me.user.name?.split(" ")[0]}</span>
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute left-full bottom-0 ml-2 w-64 bg-cream text-ink border border-ink/15 rounded-xl shadow-2xl p-4 z-50" data-testid="signed-in-popover">
            <div className="font-semibold truncate">{me.user.name}</div>
            <div className="text-[13px] text-ink/60 mb-3">{me.user.seat ? `${me.user.seat} · ` : ""}{me.role.name}</div>
            <button data-testid="logout-button" onClick={logout} className="inline-flex items-center gap-1.5 text-[14px] text-ink/75 hover:text-maroon mb-2"><LogOut className="w-4 h-4" /> Sign out</button>
            <InstallAppButton variant="light" />
            <div className="mt-2"><ReportIssue screen={active || "App"} variant="light" /></div>
          </div>
        </>
      )}
    </div>
  );
}

function RailButton({ g, on, onClick }) {
  const badge = g.items.reduce((s, i) => s + (i.badge || 0), 0);
  return (
    <button onClick={onClick} data-testid={`nav-group-${g.id}`} title={g.label} className="w-full flex flex-col items-center gap-1 py-1.5 group">
      <span className={`relative w-10 h-10 rounded-xl flex items-center justify-center transition-colors ${on ? "bg-olive text-ink" : "bg-cream/5 text-cream/80 group-hover:bg-cream/15"}`}>
        <Icon name={g.icon} className="w-[19px] h-[19px]" />
        <Badge n={badge} className="absolute -top-1.5 -right-1.5" />
      </span>
      <span className={`text-[10.5px] leading-tight text-center ${on ? "text-cream font-semibold" : "text-cream/70"}`}>{g.short || g.label}</span>
    </button>
  );
}

export default function Sidebar({ me, active, onSelect, open, onClose }) {
  const { setPersona } = useAuth();
  const rail = useRail(me);
  const [openGroup, setOpenGroup] = useState(null);
  const ref = useRef(null);
  const current = groupOfTarget(rail, active);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpenGroup(null); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const pick = (i) => {
    setOpenGroup(null); onClose();
    if (i.persona) { setPersona(i.persona); return; }
    onSelect(i.target);
  };
  const main = rail.filter((g) => g.id !== "settings");
  const settings = rail.find((g) => g.id === "settings");
  const flyGroup = rail.find((g) => g.id === openGroup);
  return (
    <>
      <aside ref={ref} data-testid="sidebar" className="hidden min-[900px]:flex relative z-30 h-full w-[84px] shrink-0 bg-ink text-cream flex-col items-stretch py-3 px-1.5">
        <img src="/logo.png" alt="Rosemoor" className="w-9 h-9 mx-auto mb-3" />
        <nav className="flex-1 overflow-y-auto space-y-1" data-testid="sidebar-nav">
          {main.map((g) => (
            <RailButton key={g.id} g={g} on={openGroup ? openGroup === g.id : current?.id === g.id}
              onClick={() => (g.id === "home" && g.items.length === 1 ? pick(g.items[0]) : setOpenGroup(openGroup === g.id ? null : g.id))} />
          ))}
        </nav>
        <div className="border-t border-cream/10 pt-2 space-y-1">
          {settings && <RailButton g={settings} on={openGroup ? openGroup === "settings" : current?.id === "settings"} onClick={() => setOpenGroup(openGroup === "settings" ? null : "settings")} />}
          <SignedInBox me={me} active={active} />
        </div>
        {flyGroup && <Flyout rail={rail} group={flyGroup} active={active} onPick={pick} onClose={() => setOpenGroup(null)} />}
      </aside>
    </>
  );
}
